import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogSelect } from "@/components/ui/dialog-select";
import { Product } from "@/types/product";
import { Plus } from "lucide-react";
import { DialogDatePicker } from "@/components/ui/dialog-date-picker";

interface AddProductDialogProps {
  customerId: string;
  onAdd: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  trigger?: React.ReactNode;
}

export function AddProductDialog({ customerId, onAdd, trigger }: AddProductDialogProps) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    deviceName: "",
    deviceDescription: "",
    salePrice: "",
    monthlyInstallment: "",
    currency: "" as "" | "IQD" | "USD",
    date: (() => {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
    })(),
    notes: "",
  });

  const currencyOptions = [
    { value: "IQD", label: "دینار (IQD)" },
    { value: "USD", label: "دۆلار (USD)" },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const salePrice = parseFloat(formData.salePrice);
    const monthlyInstallment = parseFloat(formData.monthlyInstallment);

    if (isNaN(salePrice) || salePrice <= 0) {
      alert("تکایە نرخی فرۆشتنی دروست دیاری بکە!");
      return;
    }

    if (isNaN(monthlyInstallment) || monthlyInstallment <= 0) {
      alert("تکایە قیستی مانگانەی دروست دیاری بکە!");
      return;
    }

    // چێککردنی ئەوەی قیستی مانگانە لە نرخی فرۆشتن زیاتر نەبێت
    if (monthlyInstallment > salePrice) {
      alert("قیستی مانگانە ناتوانێت لە نرخی فرۆشتن زیاتر بێت!");
      return;
    }

    onAdd({
      customerId,
      deviceName: formData.deviceName,
      deviceDescription: formData.deviceDescription,
      salePrice: salePrice,
      monthlyInstallment: monthlyInstallment,
      installments: 0,
      currency: formData.currency as "IQD" | "USD",
      paidAmount: 0,
      date: (() => {
        const d = formData.date;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })(),
      notes: formData.notes,
    });
    setFormData({
      deviceName: "",
      deviceDescription: "",
      salePrice: "",
      monthlyInstallment: "",
      currency: "",
      date: (() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
      })(),
      notes: "",
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            زیادکردنی کاڵا
            <Plus className="h-4 w-4 mr-2" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>زیادکردنی کاڵای نوێ</DialogTitle>
          <DialogDescription>زانیاریەکانی کاڵا پڕ بکەوە</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deviceName">ناوی ئامێر *</Label>
              <Input
                id="deviceName"
                value={formData.deviceName}
                onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                required
                placeholder="ناوی ئامێر"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deviceDescription">وەسفی ئامێر</Label>
              <Input
                id="deviceDescription"
                value={formData.deviceDescription}
                onChange={(e) => setFormData({ ...formData, deviceDescription: e.target.value })}
                placeholder="وەسفی ئامێر"
              />
            </div>
          </div>

          {/* دراو */}
          <div className="space-y-2">
            <Label>دراو *</Label>
            <DialogSelect
              value={formData.currency}
              onChange={(value) => setFormData({ ...formData, currency: value as "IQD" | "USD" })}
              options={currencyOptions}
              placeholder="هەڵبژێرە"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="salePrice">نرخی فرۆشتن *</Label>
              <Input
                id="salePrice"
                type="number"
                value={formData.salePrice}
                onChange={(e) => setFormData({ ...formData, salePrice: e.target.value })}
                required
                placeholder="0"
                dir="ltr"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="monthlyInstallment">قیستی مانگانە *</Label>
              <Input
                id="monthlyInstallment"
                type="number"
                value={formData.monthlyInstallment}
                onChange={(e) => setFormData({ ...formData, monthlyInstallment: e.target.value })}
                required
                placeholder="0"
                dir="ltr"
              />
            </div>
          </div>

          <div className="space-y-2">
            <DialogDatePicker
              value={formData.date}
              onChange={(date) => setFormData({ ...formData, date })}
              label="بەرواری کڕین *"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">تێبینی</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="تێبینی..."
              rows={2}
            />
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              پاشگەزبوونەوە
            </Button>
            <Button type="submit">زیادکردن</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
